import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    Legend
} from "recharts";

import {
    FaBolt,
    FaCloud,
    FaGaugeHigh,
    FaDroplet,
    FaTrash,
    FaBuilding
} from "react-icons/fa6";

import {
    FaCheckDouble,
    FaPaperPlane,
    FaPenToSquare,
    FaRotateLeft,
    FaClock,
    FaListCheck
} from "react-icons/fa6";

import apiClient from "../../shared/API/apiClient";

import {
    useReportingYear
} from "../../context/ReportingYearContext";

const ICONS = {
    energy: FaBolt,
    emissions: FaCloud,
    intensity: FaGaugeHigh,
    water: FaDroplet,
    waste: FaTrash
};

const formatNumber = value => {

    if(
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return "-";
    }

    return Number(value).toLocaleString(
        "en-IN",
        {
            maximumFractionDigits: 2
        }
    );
};

const EsgScorecardPage = () => {

    const { reportingYear, reportingYearId } = useReportingYear();
    const [reportingYearInfo, setReportingYearInfo] =useState(null);
    const [loading, setLoading] = useState(false);
    const [report, setReport] = useState(null);
    const [legalEntities, setLegalEntities] = useState([]);
    const [selectedEntity, setSelectedEntity] = useState("");
    const [selectedTrend,setSelectedTrend]=useState("energy");
    const [selectedBenchmark, setSelectedBenchmark] = useState("energy");
    const navigate = useNavigate();
    // =====================================
    // ENTITY
    // =====================================
useEffect(() => {

   const loadReportingYear = async () => {

    try {

        const res =
            await apiClient.post(
                "/api/reportingyear"
            );

        if (res.data.code === 200) {

            const selected =
                (res.data.data || [])
                    .find(
                        y =>
                            Number(y.id) ===
                            Number(reportingYearId)
                    );

            setReportingYearInfo(
                selected || null
            );
        }

    } catch (e) {

        console.error(
            "Reporting year API error:",
            e
        );
    }
};

    loadReportingYear();

}, [reportingYearId]);
    useEffect(() => {

        const load = async () => {

            if(!reportingYearId) {
                return;
            }

            try {

                const res =
                    await apiClient.get(
                        "/api/legal-entities-by-year",
                        {
                            params: {
                                reporting_year_id:
                                    reportingYearId
                            }
                        }
                    );

                setLegalEntities(
                    res.data.data || []
                );

            } catch {

                setLegalEntities([]);
            }
        };

        load();

    }, [reportingYearId]);

    // =====================================
    // FILTER
    // =====================================

    const [filters, setFilters] =
        useState({
            filter_type: "THIS_YEAR",
            quarter: "",
            month: "",
            from_date: "",
            to_date: ""
        });

      const handleFilterChange = (field, value) => {

    const updated = {
        ...filters,
        [field]: value
    };

    if(field === "filter_type") {

        updated.quarter = "";
        updated.month = "";

        if(value === "CUSTOM" && reportingYearInfo) {
            updated.from_date =
                reportingYearInfo.start_date;

            updated.to_date =
                reportingYearInfo.end_date;
        }

        if(value !== "CUSTOM") {
            updated.from_date = "";
            updated.to_date = "";
        }
    }

    setFilters(updated);
};

    // =====================================
    // LOAD
    // =====================================

    const loadReport = async () => {

        if(
            !reportingYearId ||
            !selectedEntity
        ) {
            return;
        }

        try {

            setLoading(true);

            const response =
            await apiClient.get(
                "/api/reports/esgsscorecard-dashboard",
                {
                    params: {

                        reporting_year_id:reportingYearId,
                        legal_entity_id:selectedEntity
                    }
                }
            );

        setReport(response.data);

        } finally {

            setLoading(false);
        }
    };

    useEffect(() => {

        loadReport();

    }, [
        reportingYearId,
        selectedEntity
    ]);

    if(loading) {

        return (
            <div className="dash-loader-wrapper">
                <div className="dash-loader" />
            </div>
        );
    }
    return (

    <div className="dash-page">

        <div className="dash-page-title">
            ESG Scorecard
        </div>

        <div className="dash-page-subtitle">
             Executive ESG Performance Assessment & Submission Readiness
        </div>

        {/* FILTER */}

        <div className="filter-card">
            <div className="filter-group">
                <label>Legal Entity</label>
                <select
                    className="filter-fields"
                    value={selectedEntity}
                    onChange={e => setSelectedEntity(e.target.value)}
                >
                    <option value="">Select</option>
                    {legalEntities.map(e => (
                        <option key={e.entity_id} value={e.entity_id}>
                            {e.entity_name}
                        </option>
                    ))}
                </select>
            </div>

           
        </div>
        {
                !report && (

                    <div className="empty-entity">
          <div className="empty-entity__icon"><i className="fa-solid fa-building" /></div>
          <div className="empty-entity__title">Select a Legal Entity to continue</div>
          <div className="empty-entity__sub">
            Choose a legal entity to view the Executive Dashboard.
          </div>
          
          
        
        </div>
                )
            }

        {
            report &&(() => {

                const {
                        generated_at,
                        reporting_period,
                        summary,
                        principles,
                        environment,
                        compliance,
                        risks,
                        achievements,
                        readiness
                    } = report;

                    const complianceColor =
                    compliance.completion_percentage >= 90
                        ? "#16a34a"
                        : compliance.completion_percentage >= 60
                        ? "#f59e0b"
                        : "#dc2626";
                return (

                    <>

                       <div className="reporting_year_line">

                        <strong>{reporting_period?.year || reportingYear}</strong>{" | "}
                        {legalEntities.find(e => Number(e.entity_id) === Number(selectedEntity))?.entity_name}
                        {" | Generated: "} {generated_at}

                    </div>
                        {/* KPI */}

                    <div className="dash-stat-grid" style={{ gridTemplateColumns: "repeat(6,1fr)" }}>
                        <div className="dash-stat-card">
                            <div className="dash-stat-label">Overall ESG Score</div>
                            <div className="dash-stat-value">{summary.overall_score}</div>
                            <div className="dash-stat-sub">Grade {summary.grade}</div>
                        </div>

                        <div className="dash-stat-card">
                            <div className="dash-stat-label">Health Score</div>
                            <div className="dash-stat-value">{summary.health_score}</div>
                        </div>

                        <div className="dash-stat-card">
                            <div className="dash-stat-label">Environment</div>
                            <div className="dash-stat-value">{summary.environment_score}</div>
                        </div>

                        <div className="dash-stat-card">
                            <div className="dash-stat-label">Compliance</div>
                            <div className="dash-stat-value">{summary.compliance_score}</div>
                        </div>

                        <div className="dash-stat-card">
                            <div className="dash-stat-label">Completion</div>
                            <div className="dash-stat-value">{summary.completion_percentage}%</div>
                        </div>

                        <div className="dash-stat-card">
                            <div className="dash-stat-label">Submission</div>
                            <div className="dash-stat-value">{summary.submission_status}</div>
                        </div>
                </div>
        <div className="chart-card chart-row">
                <h4>Principle Scorecard</h4>

                <table className="scorecard-table">
                    <thead>
                        <tr>
                            <th>Principle</th>
                            <th>Status</th>
                            <th>Completion</th>
                            <th>Score</th>
                        </tr>
                    </thead>

                    <tbody>
                    {principles.map(item => {
    const scoreClass =
        item.score >= 90
            ? "excellent"
            : item.score >= 70
            ? "good"
            : item.score >= 50
            ? "warning"
            : "poor";

    return (
        <tr key={item.principle_id}>
            <td>{item.principle}</td>

            <td>
                <span className={`status-pill-score ${item.status}`}>
                    <i className={`${item.status_icon} status_icon_size`} />{" "}
                    {item.status.replaceAll("_", " ")}
                </span>
            </td>

            <td>
                <div className="score-progress">
                    <div
                        className={`score-progress-fill ${scoreClass}`}
                        style={{ width: `${item.completion}%` }}
                    />
                </div>

                <small>{item.completion}%</small>
            </td>

            <td>
                <span className={`principle-score ${scoreClass}`}>
                    {item.score}
                </span>
            </td>
        </tr>
    );
})}
                        
                    </tbody>
                </table>
        </div>  

        <div className="chart-card chart-row">
    <h4>Environmental Performance</h4>

    <div
        className="dash-stat-grid"
        style={{ gridTemplateColumns: "repeat(5,1fr)" }}
    >
        <div className="dash-stat-card">
            <div className="dash-stat-header">
                <FaBolt className="dash-stat-icon" />
                <div className="dash-stat-label">Energy</div>
            </div>
            <div className="dash-stat-value">
                {formatNumber(environment.energy.value)}
            </div>
            <div className="dash-stat-sub">{environment.energy.unit}</div>
        </div>

        <div className="dash-stat-card">
            <div className="dash-stat-header">
                <FaCloud className="dash-stat-icon" />
                <div className="dash-stat-label">Emissions</div>
            </div>
            <div className="dash-stat-value">
                {formatNumber(environment.emissions.value)}
            </div>
            <div className="dash-stat-sub">{environment.emissions.unit}</div>
        </div>

        <div className="dash-stat-card">
            <div className="dash-stat-header">
                <FaGaugeHigh className="dash-stat-icon" />
                <div className="dash-stat-label">Emission Intensity</div>
            </div>
            <div className="dash-stat-value">
                {formatNumber(environment.emission_intensity.value)}
            </div>
            <div className="dash-stat-sub">
                {environment.emission_intensity.unit}
            </div>
        </div>

        <div className="dash-stat-card">
            <div className="dash-stat-header">
                <FaDroplet className="dash-stat-icon" />
                <div className="dash-stat-label">Water</div>
            </div>
            <div className="dash-stat-value">
                {formatNumber(environment.water.value)}
            </div>
            <div className="dash-stat-sub">{environment.water.unit}</div>
        </div>

        <div className="dash-stat-card">
            <div className="dash-stat-header">
                <FaTrash className="dash-stat-icon" />
                <div className="dash-stat-label">Waste</div>
            </div>
            <div className="dash-stat-value">
                {formatNumber(environment.waste.value)}
            </div>
            <div className="dash-stat-sub">{environment.waste.unit}</div>
        </div>
    </div>
</div>   
<div className="chart-card">
    <h4>Compliance Overview</h4>

    <div className="progress-text">
        Overall Compliance
        <strong>{compliance.completion_percentage}%</strong>
    </div>

    <div className="compliance-progress">
        <div
            className="compliance-progress-fill-readiness"
            style={{
                        width: `${compliance.completion_percentage}%`,
                        background: complianceColor
                    }}
        />
    </div>

    <div
        className="dash-stat-grid"
        style={{
            gridTemplateColumns: "repeat(3,1fr)"}} >
        <div className="dash-stat-card">
            <div className="dash-stat-header">
                <i className="fa-solid fa-check-double dash-stat-icon" />
                <div className="dash-stat-label">Completed</div>
            </div>

           <div className="dash-stat-value" style={{ color: "#16a34a" }}>
                {compliance.completed}
        </div>

                <div className="dash-stat-sub">
                    of {compliance.total_principles} Principles
                </div>
        </div>

        <div className="dash-stat-card">
            <div className="dash-stat-header">
                <i className="fa-solid fa-hourglass-half dash-stat-icon" />
                <div className="dash-stat-label">In Progress</div>
            </div>

            <div
    className="dash-stat-value"
    style={{ color: "#f59e0b" }}
>
    {compliance.in_progress}
</div>

<div className="dash-stat-sub">
    of {compliance.total_principles} Principles
</div>
        </div>

        <div className="dash-stat-card">
            <div className="dash-stat-header">
                <i className="fa-solid fa-clock dash-stat-icon" />
                <div className="dash-stat-label">Pending</div>
            </div>

            <div
    className="dash-stat-value"
    style={{
        color:
            compliance.pending > 0
                ? "#dc2626"
                : "#6b7280"
    }}
>
    {compliance.pending}
</div>

<div className="dash-stat-sub">
    of {compliance.total_principles} Principles
</div>
        </div>
    </div>
</div>
<div className="chart-card chart-row">
    <h4>Submission Readiness</h4>

    <div className="progress-text">
        Readiness Score
        <strong>{readiness.completion_percentage}%</strong>
    </div>

    <div className="compliance-progress">
        <div
                    className="compliance-progress-fill"
                    style={{
                        width: `${readiness.completion_percentage}%`,
                        background: complianceColor
                    }}
                />
    </div>

    <div
        className={`recommendation-card ${
            readiness.ready_for_submission ? "low" : "high"
        }`}
        
    >
        <div className="recommendation-title">
            {readiness.ready_for_submission
                ? "✅ Ready for Submission"
                : "❌ Not Ready for Submission"}
        </div>

        <div className="recommendation-message">
            {readiness.ready_for_submission
                ? "All mandatory disclosures have been completed. The Legal Entity is ready for BRSR submission."
                : "The following items must be resolved before submission."}
        </div>

        {!readiness.ready_for_submission &&
            readiness.blocking_items.length > 0 && (
                <ul style={{ paddingLeft: 20 }}>
                    {readiness.blocking_items.map((item, index) => (
                        <li key={index}>{item}</li>
                    ))}
                </ul>
            )}

        <div style={{ marginTop: 20, fontWeight: 600 }}>
            Next Action :
            <span style={{ color: "#2563eb", marginLeft: 8 }}>
                {readiness.next_action}
            </span>
        </div>
    </div>
</div>
<div className="chart-row">
    <div className="chart-card">
    <h4>Achievements ({achievements.total})</h4>

    <div className="alerts-list">
        {achievements.items.map((item, index) => (
            <div
                key={index}
                className="alert-card success"
                style={{ borderLeft: `5px solid ${item.color}` }}
            >
                <div className="alert-left">
                    <i
                        className={`${item.icon} status_icon_size`}
                        style={{ color: item.color }}
                    />

                    <div>
                        <div className="alert-title">{item.title}</div>
                        <div className="alert-message">
                            {item.description}
                        </div>
                    </div>
                </div>
            </div>
        ))}
    </div>
</div>

    <div className="chart-card">
    <h4>Risks ({risks.total})</h4>

    <div className="alerts-list">
        {risks.total === 0 ? (
            <div className="alert-card success">
                <div className="alert-left">
                    <i className="fa-solid fa-circle-check status_icon_size" />

                    <div>
                        <div className="alert-title">No Significant Risks</div>
                        <div className="alert-message">
                            No ESG risks identified.
                        </div>
                    </div>
                </div>
            </div>
        ) : (
            risks.items.map((item, index) => (
                <div
                    key={index}
                    className="alert-card"
                    style={{ borderLeft: `5px solid ${item.color}` }}
                >
                    <div className="alert-left">
                        <i
                            className={`${item.icon} status_icon_size`}
                            style={{ color: item.color }}
                        />

                        <div>
                            <div className="alert-title">{item.title}</div>
                            <div className="alert-message">
                                {item.description}
                            </div>
                        </div>
                    </div>

                    {item.action && (
                        <button
                            className="org-btn-save"
                            onClick={() =>
                                navigate(item.route, {
                                    state: {
                                        reportingYearId,
                                        legalEntityId: selectedEntity
                                    }
                                })
                            }
                        >
                            {item.action}
                        </button>
                    )}
                </div>
            ))
        )}
    </div>
</div>
</div>

                    </>
                );
            })()
        }

                    </div>
            );
            };

export default EsgScorecardPage;